import { RuntimeConfig } from '@occc/config';

export type OfferLink = {
  label: string;
  href: string;
};

type GetOfferLinksParams = {
  config: RuntimeConfig;
  offerId: string;
  requiresActivation?: boolean;
  subBrandUuids?: string[];
};

const getOfferUrl = (baseUrl?: string | null, offerId?: string) => {
  if (!baseUrl || !offerId) {
    return null;
  }

  return `${baseUrl.replace(/\/+$/, '')}/home/offers/${offerId}`;
};

const getLinksForSubBrand = ({
  subBrandName,
  baseUrl,
  offerId,
  requiresActivation,
}: {
  subBrandName: string;
  baseUrl?: string | null;
  offerId: string;
  requiresActivation: boolean;
}): OfferLink[] => {
  const offerUrl = getOfferUrl(baseUrl, offerId);

  if (!subBrandName || !offerUrl) {
    return [];
  }

  const items: OfferLink[] = [
    {
      label: `${subBrandName} Offer URL`,
      href: offerUrl,
    },
  ];

  if (requiresActivation) {
    items.push({
      label: `${subBrandName} Offer Activation URL`,
      href: `${offerUrl}?activate=true`,
    });
  }

  return items;
};

export const getOfferLinks = ({
  config,
  offerId,
  requiresActivation = false,
  subBrandUuids = [],
}: GetOfferLinksParams): OfferLink[] => {
  const subBrandsConfig = config.subBrands;
  const hasEnabledSubBrands = !!subBrandsConfig?.enabled && !!subBrandsConfig.list;

  // Sub brands disabled: use the single brand link from the config.
  if (!hasEnabledSubBrands) {
    return getLinksForSubBrand({
      subBrandName: config.brandName,
      baseUrl: config.experienceUrl,
      offerId,
      requiresActivation,
    });
  }

  // Sub brands enabled: links come only from the sub brands list (the parent
  // brand is already included there). A specific sub brand constraint narrows
  // the list to that sub brand; a universal offer (no constraint) shows all.
  const requestedSubBrandUuids = new Set(subBrandUuids.filter(Boolean));
  const targetSubBrands = requestedSubBrandUuids.size
    ? subBrandsConfig.list.filter((subBrand) => requestedSubBrandUuids.has(subBrand.uuid))
    : subBrandsConfig.list;

  return targetSubBrands.flatMap((subBrand) =>
    getLinksForSubBrand({
      subBrandName: subBrand.name,
      baseUrl: subBrand.experienceUrl,
      offerId,
      requiresActivation,
    }),
  );
};
